import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import {
  getChannels, getOrCreateDirect, getMessages,
  sendMessage, markRead, getUnreadCounts
} from '../lib/supabase';
import { sendPushToMember } from '../lib/push';
import { NAVY, GOLD, GOLD_LIGHT, WHITE, BG } from '../lib/ui';
import { Avatar } from '../lib/ui';

const NAVY_DARK = '#111E33';
const fmt = d => {
  if (!d) return '';
  const date = new Date(d);
  const now  = new Date();
  const diff = now - date;
  if (diff < 60000)  return 'just now';
  if (diff < 3600000) return `${Math.floor(diff/60000)}m ago`;
  if (diff < 86400000) return date.toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit' });
  return date.toLocaleDateString('en-GB', { day:'2-digit', month:'short' });
};
const fmtSz = b => b < 1048576 ? `${(b/1024).toFixed(1)} KB` : `${(b/1048576).toFixed(1)} MB`;

export default function MessagingView({ team, matters, currentUserEmail }) {
  const me = team.find(m => m.email === currentUserEmail);

  const [channels,     setChannels]     = useState([]);
  const [activeChannel,setActive]       = useState(null);
  const [messages,     setMessages]     = useState([]);
  const [text,         setText]         = useState('');
  const [sending,      setSending]      = useState(false);
  const [unread,       setUnread]       = useState({});
  const [showDMPicker, setDMPicker]     = useState(false);
  const [linkMatter,   setLinkMatter]   = useState('');
  const [uploading,    setUploading]    = useState(false);
  const bottomRef = useRef();
  const fileRef   = useRef();
  const inputRef  = useRef();

  // ── Load channels ──────────────────────────────────────────────────────────
  const loadChannels = useCallback(async () => {
    const chs = await getChannels();
    // Only show direct channels that include me
    const visible = chs.filter(c =>
      c.type === 'group' || (me && (c.members || []).includes(me.id))
    );
    setChannels(visible);
    if (!activeChannel && visible.length > 0) setActive(visible[0]);
    // Get unread counts
    if (me) {
      const counts = await getUnreadCounts(me.id, visible);
      setUnread(counts);
    }
  }, [me, activeChannel]);

  useEffect(() => { loadChannels(); }, [loadChannels]);

  // ── Load messages for active channel ──────────────────────────────────────
  useEffect(() => {
    if (!activeChannel) return;
    getMessages(activeChannel.id).then(msgs => {
      setMessages(msgs || []);
      scrollToBottom();
    });
    if (me) markRead(activeChannel.id, me.id);
    setUnread(u => ({ ...u, [activeChannel.id]: 0 }));
  }, [activeChannel?.id]);

  // ── Real-time subscription ─────────────────────────────────────────────────
  useEffect(() => {
    if (!activeChannel) return;
    const sub = supabase
      .channel(`messages:${activeChannel.id}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'messages',
        filter: `channel_id=eq.${activeChannel.id}`
      }, payload => {
        setMessages(ms => [...ms, payload.new]);
        scrollToBottom();
        if (me) markRead(activeChannel.id, me.id);
      })
      .subscribe();
    return () => supabase.removeChannel(sub);
  }, [activeChannel?.id, me?.id]);

  // ── Real-time for unread badges (other channels) ───────────────────────────
  useEffect(() => {
    if (!me) return;
    const sub = supabase
      .channel('all_messages')
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'messages'
      }, payload => {
        const chId = payload.new.channel_id;
        if (chId !== activeChannel?.id && payload.new.sender_name !== me.name) {
          setUnread(u => ({ ...u, [chId]: (u[chId] || 0) + 1 }));
        }
      })
      .subscribe();
    return () => supabase.removeChannel(sub);
  }, [me?.id, activeChannel?.id]);

  const scrollToBottom = () => setTimeout(() => bottomRef.current?.scrollIntoView({ behavior:'smooth' }), 50);

  // ── Send message ───────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!text.trim() && !uploading) return;
    if (!activeChannel || !me) return;
    setSending(true);
    await sendMessage({
      channel_id:  activeChannel.id,
      sender_id:   me.id,
      sender_name: me.name,
      text:        text.trim() || null,
      matter_id:   linkMatter || null,
    });
    // Send push notification to recipients
    if (activeChannel.type === 'direct') {
      const otherId = (activeChannel.members || []).find(id => id !== me.id);
      if (otherId) {
        const pushPayload = {
          title: `Message from ${me.name}`,
          body: text.trim().slice(0, 80) || 'Sent an attachment',
          view: 'messages',
        };
        await supabase.from('notifications').insert([{
          recipient_id: otherId, type: 'message',
          title: pushPayload.title, body: pushPayload.body,
          link_view: 'messages',
        }]);
        await sendPushToMember(otherId, pushPayload);
      }
    } else {
      const { data: teamMembers } = await supabase.from('team').select('id').neq('id', me.id);
      for (const member of (teamMembers || [])) {
        const pushPayload = {
          title: `${me.name} in #${channelName(activeChannel)}`,
          body: text.trim().slice(0, 80) || 'Sent an attachment',
          view: 'messages',
        };
        await supabase.from('notifications').insert([{
          recipient_id: member.id, type: 'message',
          title: pushPayload.title, body: pushPayload.body,
          link_view: 'messages',
        }]);
        await sendPushToMember(member.id, pushPayload);
      }
    }
    setText(''); setLinkMatter(''); setSending(false);
    inputRef.current?.focus();
  };

  // ── File upload ────────────────────────────────────────────────────────────
  const handleFile = async (file) => {
    if (!file || !activeChannel || !me) return;
    setUploading(true);
    const path = `messages/${activeChannel.id}/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g,'_')}`;
    const { error: upErr } = await supabase.storage.from('documents').upload(path, file);
    if (upErr) { alert(upErr.message); setUploading(false); return; }
    const { data: urlData } = await supabase.storage.from('documents').createSignedUrl(path, 60 * 60 * 24 * 7);
    await sendMessage({
      channel_id:  activeChannel.id,
      sender_id:   me.id,
      sender_name: me.name,
      text:        null,
      file_url:    urlData?.signedUrl || path,
      file_name:   file.name,
      file_size:   file.size,
      matter_id:   linkMatter || null,
    });
    setUploading(false); setLinkMatter('');
  };

  // ── Start DM ───────────────────────────────────────────────────────────────
  const startDM = async (memberId) => {
    if (!me) return;
    setDMPicker(false);
    const ch = await getOrCreateDirect([me.id, memberId]);
    setChannels(chs => chs.find(c => c.id === ch.id) ? chs : [...chs, ch]);
    setActive(ch);
  };

  // ── Channel display name ───────────────────────────────────────────────────
  const channelName = (ch) => {
    if (ch.type === 'group') return ch.name || 'General';
    const otherId = (ch.members || []).find(id => id !== me?.id);
    return team.find(m => m.id === otherId)?.name || 'Direct Message';
  };
  const channelInitials = (ch) => {
    if (ch.type === 'group') return '##';
    const otherId = (ch.members || []).find(id => id !== me?.id);
    const name = team.find(m => m.id === otherId)?.name || '?';
    return name.split(' ').map(p=>p[0]).join('').toUpperCase().slice(0,2);
  };

  const matterName = id => matters.find(m => m.id === id)?.name || '';

  // ── Group messages by date ─────────────────────────────────────────────────
  const grouped = messages.reduce((acc, msg) => {
    const day = msg.created_at?.slice(0,10) || 'unknown';
    if (!acc[day]) acc[day] = [];
    acc[day].push(msg);
    return acc;
  }, {});

  const totalUnread = Object.values(unread).reduce((a,b) => a+b, 0);

  return (
    <div style={{ display:'flex', height:'calc(100vh - 100px)', background:WHITE, borderRadius:12, border:'1px solid #E5E0D8', overflow:'hidden' }}>

      {/* ── Sidebar ── */}
      <div className="messaging-sidebar" style={{ width:240, background:NAVY_DARK, display:'flex', flexDirection:'column', flexShrink:0 }}>
        <div style={{ padding:'16px 16px 12px', borderBottom:`1px solid #1E2E48` }}>
          <div style={{ fontFamily:'Georgia,serif', fontSize:15, fontWeight:700, color:WHITE }}>
            Messages {totalUnread > 0 && <span style={{ background:'#C0392B', color:WHITE, borderRadius:20, padding:'1px 7px', fontSize:11, marginLeft:6 }}>{totalUnread}</span>}
          </div>
        </div>

        {/* Group channels */}
        <div style={{ padding:'10px 0 4px' }}>
          <div style={{ fontSize:10, color:'#4A6080', textTransform:'uppercase', letterSpacing:1.5, padding:'0 16px 6px', fontWeight:700 }}>Channels</div>
          {channels.filter(c => c.type === 'group').map(ch => (
            <button key={ch.id} onClick={() => setActive(ch)} style={{
              display:'flex', alignItems:'center', gap:8, width:'100%', padding:'8px 16px',
              background: activeChannel?.id === ch.id ? '#1E3050' : 'none',
              border:'none', cursor:'pointer', textAlign:'left', fontFamily:'inherit'
            }}>
              <span style={{ color:'#4A6080', fontSize:16 }}>#</span>
              <span style={{ fontSize:13, color: activeChannel?.id === ch.id ? WHITE : '#8AACCC', fontWeight: activeChannel?.id === ch.id ? 600 : 400, flex:1 }}>
                {channelName(ch)}
              </span>
              {(unread[ch.id] || 0) > 0 && (
                <span style={{ background:'#C0392B', color:WHITE, borderRadius:20, padding:'1px 7px', fontSize:10, fontWeight:700 }}>{unread[ch.id]}</span>
              )}
            </button>
          ))}
        </div>

        {/* Direct messages */}
        <div style={{ padding:'10px 0 4px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0 16px 6px' }}>
            <span style={{ fontSize:10, color:'#4A6080', textTransform:'uppercase', letterSpacing:1.5, fontWeight:700 }}>Direct Messages</span>
            <button onClick={() => setDMPicker(p => !p)} style={{ background:'none', border:'none', color:'#4A6080', cursor:'pointer', fontSize:18, lineHeight:1, padding:0 }}>+</button>
          </div>
          {channels.filter(c => c.type === 'direct').map(ch => (
            <button key={ch.id} onClick={() => setActive(ch)} style={{
              display:'flex', alignItems:'center', gap:8, width:'100%', padding:'7px 16px',
              background: activeChannel?.id === ch.id ? '#1E3050' : 'none',
              border:'none', cursor:'pointer', textAlign:'left', fontFamily:'inherit'
            }}>
              <div style={{ width:24, height:24, borderRadius:'50%', background:GOLD+'33', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, color:GOLD, flexShrink:0 }}>
                {channelInitials(ch)}
              </div>
              <span style={{ fontSize:13, color: activeChannel?.id === ch.id ? WHITE : '#8AACCC', fontWeight: activeChannel?.id === ch.id ? 600 : 400, flex:1 }}>
                {channelName(ch)}
              </span>
              {(unread[ch.id] || 0) > 0 && (
                <span style={{ background:'#C0392B', color:WHITE, borderRadius:20, padding:'1px 7px', fontSize:10, fontWeight:700 }}>{unread[ch.id]}</span>
              )}
            </button>
          ))}
        </div>

        {/* DM picker */}
        {showDMPicker && (
          <div style={{ margin:'0 10px 8px', background:'#1E2E48', borderRadius:8, padding:8 }}>
            <div style={{ fontSize:11, color:'#8AACCC', marginBottom:6, padding:'0 4px' }}>Start a conversation with:</div>
            {team.filter(m => m.id !== me?.id).map(m => (
              <button key={m.id} onClick={() => startDM(m.id)} style={{
                display:'flex', alignItems:'center', gap:8, width:'100%', padding:'6px 8px',
                background:'none', border:'none', cursor:'pointer', borderRadius:6, fontFamily:'inherit'
              }}
                onMouseEnter={e => e.currentTarget.style.background='#2A3F60'}
                onMouseLeave={e => e.currentTarget.style.background='none'}>
                <Avatar name={m.name} size={22} />
                <span style={{ fontSize:12, color:WHITE }}>{m.name}</span>
              </button>
            ))}
          </div>
        )}

        {/* Me indicator */}
        {me && (
          <div style={{ marginTop:'auto', padding:'10px 16px', borderTop:'1px solid #1E2E48', display:'flex', alignItems:'center', gap:8 }}>
            <div style={{ width:8, height:8, borderRadius:'50%', background:'#27AE60', flexShrink:0 }} />
            <span style={{ fontSize:12, color:'#8AACCC', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{me.name}</span>
          </div>
        )}
      </div>

      {/* ── Main chat area ── */}
      <div className="messaging-main" style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0 }}>

        {/* Mobile channel switcher — only visible on mobile */}
        <div style={{ display:'none' }} className="mobile-channel-bar">
          <div style={{ padding:'8px 12px', borderBottom:'1px solid #F0EDE8', background:'#F7F5F0', overflowX:'auto', whiteSpace:'nowrap', display:'flex', alignItems:'center', gap:8 }}>
            {channels.map(ch => (
              <button key={ch.id} onClick={() => setActive(ch)} style={{
                display:'inline-block', padding:'5px 12px', borderRadius:20,
                background: activeChannel?.id === ch.id ? '#1B2A4A' : WHITE,
                color: activeChannel?.id === ch.id ? '#B59A6A' : '#555',
                border:'1px solid #D0CBBF', cursor:'pointer', fontSize:12, fontWeight:600,
                fontFamily:'inherit', whiteSpace:'nowrap', flexShrink:0,
              }}>
                {ch.type === 'group' ? `# ${channelName(ch)}` : channelName(ch)}
                {(unread[ch.id] || 0) > 0 && <span style={{ background:'#C0392B', color:WHITE, borderRadius:20, padding:'1px 6px', fontSize:10, marginLeft:5 }}>{unread[ch.id]}</span>}
              </button>
            ))}
            {/* New DM button */}
            <button onClick={() => setDMPicker(p => !p)} style={{
              display:'inline-flex', alignItems:'center', gap:4, padding:'5px 12px',
              borderRadius:20, background:'#1B2A4A', color:'#B59A6A',
              border:'none', cursor:'pointer', fontSize:12, fontWeight:700,
              fontFamily:'inherit', whiteSpace:'nowrap', flexShrink:0,
            }}>+ DM</button>
          </div>
          {/* DM picker dropdown for mobile */}
          {showDMPicker && (
            <div style={{ background:WHITE, borderBottom:'1px solid #F0EDE8', padding:'8px 12px', display:'flex', gap:8, flexWrap:'wrap' }}>
              {team.filter(m => m.id !== me?.id).map(m => (
                <button key={m.id} onClick={() => startDM(m.id)} style={{
                  display:'flex', alignItems:'center', gap:6, padding:'6px 12px',
                  background:'#F7F5F0', border:'1px solid #E5E0D8', borderRadius:20,
                  cursor:'pointer', fontSize:12, fontFamily:'inherit', color:'#1B2A4A', fontWeight:600,
                }}>
                  <Avatar name={m.name} size={20} />
                  {m.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Channel header */}
        {activeChannel && (
          <div style={{ padding:'14px 20px', borderBottom:'1px solid #F0EDE8', display:'flex', alignItems:'center', gap:10, background:WHITE, flexShrink:0 }}>
            <span style={{ fontSize:16, color:NAVY, fontWeight:700 }}>
              {activeChannel.type === 'group' ? '#' : ''} {channelName(activeChannel)}
            </span>
            {activeChannel.type === 'group' && (
              <span style={{ fontSize:12, color:'#AAA' }}>· {team.length} members</span>
            )}
          </div>
        )}

        {/* Messages */}
        <div style={{ flex:1, overflowY:'auto', padding:'16px 20px', display:'flex', flexDirection:'column', gap:0 }}>
          {!activeChannel && (
            <div style={{ textAlign:'center', color:'#BBB', marginTop:60, fontSize:14 }}>Select a channel or start a direct message</div>
          )}
          {Object.entries(grouped).map(([day, dayMsgs]) => (
            <div key={day}>
              {/* Date divider */}
              <div style={{ display:'flex', alignItems:'center', gap:10, margin:'16px 0 12px' }}>
                <div style={{ flex:1, height:1, background:'#F0EDE8' }} />
                <span style={{ fontSize:11, color:'#CCC', fontWeight:600 }}>
                  {new Date(day).toLocaleDateString('en-GB', { weekday:'long', day:'2-digit', month:'long' })}
                </span>
                <div style={{ flex:1, height:1, background:'#F0EDE8' }} />
              </div>
              {dayMsgs.map((msg, i) => {
                const isMe = msg.sender_name === me?.name;
                const prevMsg = dayMsgs[i-1];
                const sameAuthor = prevMsg && prevMsg.sender_name === msg.sender_name;
                const matter = msg.matter_id ? matters.find(m => m.id === msg.matter_id) : null;
                return (
                  <div key={msg.id} style={{ marginBottom: sameAuthor ? 2 : 12 }}>
                    {!sameAuthor && (
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                        <Avatar name={msg.sender_name} size={28} />
                        <span style={{ fontSize:13, fontWeight:700, color:NAVY }}>{msg.sender_name}</span>
                        <span style={{ fontSize:11, color:'#CCC' }}>{fmt(msg.created_at)}</span>
                      </div>
                    )}
                    <div style={{ paddingLeft: sameAuthor ? 36 : 36 }}>
                      {/* Matter link badge */}
                      {matter && (
                        <div style={{ display:'inline-flex', alignItems:'center', gap:4, background:`${GOLD}18`, border:`1px solid ${GOLD}44`, borderRadius:20, padding:'2px 8px', fontSize:11, color:GOLD, marginBottom:4 }}>
                          📁 {matter.name}
                        </div>
                      )}
                      {/* Text message */}
                      {msg.text && (
                        <div style={{
                          display:'inline-block', maxWidth:'75%',
                          background: isMe ? NAVY : '#F4F2EE',
                          color: isMe ? GOLD_LIGHT : NAVY,
                          borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                          padding:'9px 14px', fontSize:13, lineHeight:1.6,
                        }}>
                          {msg.text}
                        </div>
                      )}
                      {/* File attachment */}
                      {msg.file_name && (
                        <div style={{ display:'inline-flex', alignItems:'center', gap:10, background:'#F4F2EE', borderRadius:10, padding:'10px 14px', maxWidth:'75%' }}>
                          <span style={{ fontSize:22 }}>📎</span>
                          <div style={{ minWidth:0 }}>
                            <div style={{ fontSize:12, fontWeight:600, color:NAVY, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:200 }}>{msg.file_name}</div>
                            {msg.file_size && <div style={{ fontSize:11, color:'#AAA' }}>{fmtSz(msg.file_size)}</div>}
                          </div>
                          {msg.file_url && (
                            <button onClick={() => { window.location.href = msg.file_url; }} style={{ background:NAVY, color:GOLD, border:'none', borderRadius:6, padding:'6px 12px', cursor:'pointer', fontSize:11, fontWeight:700, fontFamily:'inherit', flexShrink:0 }}>↓</button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        {/* Input area */}
        {activeChannel && me && (
          <div style={{ padding:'12px 16px', borderTop:'1px solid #F0EDE8', background:WHITE, flexShrink:0 }}>
            {/* Matter link selector */}
            <div style={{ display:'flex', gap:8, marginBottom:8, alignItems:'center' }}>
              <select value={linkMatter} onChange={e => setLinkMatter(e.target.value)} style={{ fontSize:11, border:'1px solid #E5E0D8', borderRadius:6, padding:'4px 8px', color:'#888', background:'#FAFAF8', fontFamily:'inherit', flex:1, maxWidth:220 }}>
                <option value="">Link to a matter (optional)</option>
                {matters.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
              {linkMatter && (
                <button onClick={() => setLinkMatter('')} style={{ background:'none', border:'none', color:'#CCC', cursor:'pointer', fontSize:16 }}>×</button>
              )}
            </div>
            {/* Message input row */}
            <div style={{ display:'flex', gap:8, alignItems:'flex-end' }}>
              {/* File attach */}
              <input ref={fileRef} type="file" style={{ display:'none' }} onChange={e => e.target.files[0] && handleFile(e.target.files[0])} />
              <button onClick={() => fileRef.current?.click()} disabled={uploading} style={{ background:'#F4F2EE', border:'none', borderRadius:8, padding:'10px 12px', cursor:'pointer', fontSize:18, color:'#888', flexShrink:0, minHeight:44 }}>
                {uploading ? '⏳' : '📎'}
              </button>
              {/* Text input */}
              <textarea
                ref={inputRef}
                value={text}
                onChange={e => setText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                placeholder={`Message ${activeChannel.type === 'group' ? '#' + channelName(activeChannel) : channelName(activeChannel)}…`}
                rows={1}
                style={{ flex:1, border:'1px solid #E5E0D8', borderRadius:10, padding:'10px 14px', fontSize:14, fontFamily:'inherit', resize:'none', outline:'none', background:'#FAFAF8', maxHeight:120, lineHeight:1.5 }}
              />
              {/* Send */}
              <button onClick={handleSend} disabled={(!text.trim() && !uploading) || sending} style={{ background: text.trim() ? NAVY : '#E5E0D8', color: text.trim() ? GOLD : '#AAA', border:'none', borderRadius:8, padding:'10px 16px', cursor: text.trim() ? 'pointer' : 'default', fontSize:16, fontWeight:700, flexShrink:0, minHeight:44, transition:'background 0.15s' }}>
                ➤
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
